import { type ResolveAsset, SceneRenderer } from "@cva/renderer";
import {
  type BlockBuildOptions,
  boundingRect,
  buildBlock,
  DEFAULT_BLOCK_MS,
  elementBox,
  type LibraryBlock,
  type LibraryItem,
  type PlacedElements,
  paintToCss,
  placeSavedElements,
  type SceneDocument,
  unionRects,
} from "@cva/shared";
import { type ReactNode, type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useEnv } from "./env";
import { useEditor } from "./store";

/**
 * Preview tiles of building blocks, shared by the blocks panel and the
 * fullscreen browser: built and drawn only once they scroll into view, the
 * builds cached per set of build options, the animation played on hover.
 */

/** Behind the previews in videos: a stand-in for the video picture. */
export const VIDEO_BACKDROP = "linear-gradient(160deg, #71717a 0%, #27272a 60%, #3f3f46 100%)";

const CHECKERBOARD = "repeating-conic-gradient(#f4f4f5 0% 25%, #ffffff 0% 50%) 50% / 14px 14px";

/** Built previews per build options object (panel and browser share them). */
const previewCache = new WeakMap<object, Map<string, PlacedElements>>();

/** The block built for its preview (at 0 s), cached while the build options stay the same. */
export function blockPreview(
  block: LibraryBlock,
  options: Omit<BlockBuildOptions, "at">,
): PlacedElements {
  let builds = previewCache.get(options);
  if (!builds) {
    builds = new Map();
    previewCache.set(options, builds);
  }
  let placed = builds.get(block.id);
  if (!placed) {
    placed = buildBlock(block, { ...options, at: 0 });
    builds.set(block.id, placed);
  }
  return placed;
}

/** Loops `0 … duration` while active (hover preview), undefined otherwise. */
export function useLoopTime(active: boolean, duration: number): number | undefined {
  const [time, setTime] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (!active) {
      setTime(undefined);
      return;
    }
    const started = performance.now();
    let frame = 0;
    const loop = () => {
      const t = (performance.now() - started) % (duration + 700);
      setTime(Math.min(t, duration - 1));
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [active, duration]);
  return time;
}

/** True once the element has come near the visible area (stays true). */
export function useInView<T extends Element>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || visible) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
      },
      { rootMargin: "300px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);
  return [ref, visible];
}

/** The placed elements drawn alone, zoomed onto them (collages: the whole page). */
export function PlacedPreview({
  placed,
  format,
  kind,
  fill,
  background,
  time,
  resolveAsset,
  width,
  height,
}: {
  placed: PlacedElements;
  format: { width: number; height: number };
  kind: "design" | "video";
  fill: boolean;
  background: string;
  time: number | undefined;
  resolveAsset: ResolveAsset;
  width: number;
  height: number;
}) {
  const doc = useMemo<SceneDocument>(
    () => ({
      schemaVersion: 1,
      kind,
      format: { preset: "custom", width: format.width, height: format.height, print: null },
      pages: [{ id: "preview", background: "#00000000", children: placed.roots.map((r) => r.id) }],
      elements: Object.fromEntries(placed.elements.map((element) => [element.id, element])),
    }),
    [placed, format.width, format.height, kind],
  );
  const bounds = fill
    ? { x: 0, y: 0, width: format.width, height: format.height }
    : (unionRects(placed.roots.map((root) => boundingRect(elementBox(root)))) ?? {
        x: 0,
        y: 0,
        width: format.width,
        height: format.height,
      });
  const pad = fill ? 0 : Math.max(bounds.width, bounds.height) * 0.08;
  const scale = Math.min(width / (bounds.width + pad * 2), height / (bounds.height + pad * 2));
  const left = (width - bounds.width * scale) / 2 - bounds.x * scale;
  const top = (height - bounds.height * scale) / 2 - bounds.y * scale;
  return (
    <div className="relative overflow-hidden" style={{ width, height, background }}>
      {fill && kind === "video" ? (
        // The page area of a collage or frame on the stand-in picture.
        <div
          className="absolute"
          style={{
            left,
            top,
            width: format.width * scale,
            height: format.height * scale,
            background: VIDEO_BACKDROP,
          }}
        />
      ) : null}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          transform: `translate(${left}px, ${top}px) scale(${scale})`,
          transformOrigin: "0 0",
          pointerEvents: "none",
        }}
      >
        <SceneRenderer
          document={doc}
          clip={fill}
          transparent
          time={time}
          animate={time !== undefined}
          resolveAsset={resolveAsset}
        />
      </div>
    </div>
  );
}

/** Background of a tile: the stand-in picture (video), the page colour (collages) or a checkerboard. */
function useTileBackground(fill: boolean): string {
  const kind = useEditor((s) => s.doc.kind);
  const page = useEditor((s) => s.doc.pages[s.pageIndex]?.background);
  if (kind === "video") return fill ? "#e4e4e7" : VIDEO_BACKDROP;
  return fill ? paintToCss(page ?? "#ffffff") : CHECKERBOARD;
}

export function BlockTile({
  title,
  build,
  duration,
  fill,
  onInsert,
  actions,
  width,
  height,
  large = false,
  buildKey,
}: {
  title: string;
  /** Builds the preview; called once the tile is (nearly) visible. */
  build: () => PlacedElements;
  /** What the build depends on besides the page format (built again when it changes). */
  buildKey: unknown;
  duration: number;
  fill: boolean;
  onInsert: () => void;
  actions?: ReactNode;
  width: number;
  height: number;
  /** Fullscreen browser: larger label. */
  large?: boolean;
}) {
  const env = useEnv();
  const kind = useEditor((s) => s.doc.kind);
  const format = useEditor((s) => s.doc.format);
  const background = useTileBackground(fill);
  const [ref, visible] = useInView<HTMLDivElement>();
  const [hover, setHover] = useState(false);
  const time = useLoopTime(hover && visible && kind === "video", duration);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `build` is a new closure on every render; `buildKey` and the format are what it depends on.
  const placed = useMemo(() => (visible ? build() : null), [visible, buildKey, format, kind]);
  return (
    <div ref={ref} className="group relative" style={{ width }}>
      <button
        type="button"
        onClick={onInsert}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        title={`${title} einfügen`}
        className="block w-full overflow-hidden rounded-lg border border-zinc-200 text-left transition hover:border-brand-300 hover:ring-2 hover:ring-brand-400 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:outline-none"
      >
        {placed ? (
          <PlacedPreview
            placed={placed}
            format={format}
            kind={kind}
            fill={fill}
            background={background}
            time={time}
            resolveAsset={env.resolveAsset}
            width={width}
            height={height}
          />
        ) : (
          <div style={{ width, height, background }} />
        )}
        <span
          className={
            large
              ? "block truncate border-t border-zinc-100 bg-white px-3 py-2 text-sm text-zinc-800"
              : "block truncate border-t border-zinc-100 bg-white px-2 py-1 text-[11px] text-zinc-700"
          }
        >
          {title}
        </span>
      </button>
      {actions ? (
        <span className="absolute top-1 right-1 flex gap-0.5 opacity-0 transition group-hover:opacity-100">
          {actions}
        </span>
      ) : null}
    </div>
  );
}

/** Tile of a built-in block. */
export function LibraryBlockTile({
  block,
  buildOptions,
  onInsert,
  width,
  height,
  large,
}: {
  block: LibraryBlock;
  buildOptions: Omit<BlockBuildOptions, "at">;
  onInsert: () => void;
  width: number;
  height: number;
  large?: boolean;
}) {
  return (
    <BlockTile
      title={block.title}
      build={() => blockPreview(block, buildOptions)}
      duration={block.duration ?? DEFAULT_BLOCK_MS}
      fill={block.anchor === "fill"}
      onInsert={onInsert}
      width={width}
      height={height}
      large={large}
      // A new kit, format or font measurement builds the preview again.
      buildKey={buildOptions}
    />
  );
}

/** Tile of a saved block ("Meine Bausteine"). */
export function SavedBlockTile({
  item,
  frame,
  kind,
  onInsert,
  actions,
  width,
  height,
  large,
}: {
  item: Extract<LibraryItem, { kind: "element" }>;
  frame: { width: number; height: number };
  kind: "design" | "video";
  onInsert: () => void;
  actions?: ReactNode;
  width: number;
  height: number;
  large?: boolean;
}) {
  return (
    <BlockTile
      title={item.name}
      build={() => placeSavedElements(item.data, { ...frame, kind }, 0)}
      duration={item.data.duration ?? DEFAULT_BLOCK_MS}
      fill={false}
      onInsert={onInsert}
      actions={actions}
      width={width}
      height={height}
      large={large}
      buildKey={item.data}
    />
  );
}
